import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Train, UserPlus, Search, Printer } from "lucide-react";
import { printTrenitaliaSummary } from "@/components/admin/printTrenitaliaTicket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TrenitaliaClientCard } from "@/components/admin/TrenitaliaClientCard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminTrenitalia() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", points: "" });
  const [search, setSearch] = useState("");
  const auth = localStorage.getItem("adminAuth");
  const headers = { Authorization: `Basic ${auth}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/trenitalia/clients`, { headers: { Authorization: `Basic ${auth}` } });
      setClients(res.data);
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin");
    }
  }, [auth, navigate]);

  useEffect(() => {
    if (!auth) { navigate("/admin"); return; }
    load();
  }, [auth, load, navigate]);

  const addClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/trenitalia/clients`, { ...form, points: parseInt(form.points, 10) || 0 }, { headers });
      toast.success("Client ajouté");
      setForm({ first_name: "", last_name: "", points: "" });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const q = search.toLowerCase();
  const filtered = clients.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(q));
  const totalPoints = clients.reduce((s, c) => s + c.points, 0);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="trenitalia-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/admin/dashboard")} data-testid="trenitalia-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
          <Train className="w-7 h-7 text-emerald-600" />
          <h1 className="text-2xl md:text-3xl font-bold">Trenitalia</h1>
          <span className="ml-auto text-sm text-slate-500" data-testid="trenitalia-total">{clients.length} client(s) — {totalPoints} pts au total</span>
          <Button variant="outline" onClick={() => printTrenitaliaSummary(clients)} disabled={clients.length === 0} data-testid="trenitalia-print-all-btn"><Printer className="w-4 h-4 mr-1" />Imprimer la liste</Button>
        </div>
        <form onSubmit={addClient} className="bg-white rounded-xl border p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2" data-testid="trenitalia-add-form">
          <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="trenitalia-last-name" />
          <Input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="trenitalia-first-name" />
          <Input type="number" min="0" placeholder="Pts initiaux" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} data-testid="trenitalia-initial-points" />
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="trenitalia-add-btn"><UserPlus className="w-4 h-4 mr-1" />Ajouter</Button>
        </form>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input className="pl-9" placeholder="Rechercher un client..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="trenitalia-search" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((c) => <TrenitaliaClientCard key={c.id} client={c} headers={headers} onChange={load} />)}
        </div>
        {filtered.length === 0 && <p className="text-center text-slate-400">Aucun client</p>}
      </div>
    </div>
  );
}
